import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.addEventListener('pageshow', this.#normalizeDialogState);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    this.#normalizeDialogState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.removeEventListener('pageshow', this.#normalizeDialogState);
    this.#historyAbortController?.abort();
  }

  /**
   * Recovers from a dialog that carries `open` without having been opened via
   * showModal().
   *
   * Such a dialog is not in the top layer, and the top layer is what lets a
   * modal escape its ancestors' containing blocks. Without it, `height: 100%`
   * resolves against a nearby ancestor instead of the viewport and the
   * `.cart-drawer__dialog:modal` rule that supplies `max-height: 100dvh` never
   * matches — so the drawer paints as a ~400x66 stub near the header, with no
   * backdrop, showing only its heading row.
   *
   * The state is reachable without the theme doing anything wrong: a bfcache
   * restore replays the serialised DOM with `open` still set but does not
   * restore top-layer membership, and any script (an app embed, an automation)
   * calling `show()` rather than `showModal()` lands in the same place. Rather
   * than try to enumerate the producers, detect the inconsistency and reset it.
   *
   * Closing rather than re-opening is deliberate: reaching this state means the
   * shopper arrived at a page they did not open the drawer on, and the mobile
   * popstate path already treats a back-navigation as "close the drawer".
   */
  #normalizeDialogState = () => {
    const dialog = this.refs.dialog;
    if (!dialog?.hasAttribute('open') || dialog.matches(':modal')) return;

    dialog.close();
    dialog.classList.remove('dialog-closing');
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      // dialog.close() has just put focus back on the trigger. Popping the entry
      // we pushed lands on the previous URL, and when that URL carries a fragment
      // (a keyboard user took the skip link) the browser re-runs the fragment
      // navigation and blurs everything. Put focus back where close() left it.
      const restored = document.activeElement;
      window.addEventListener(
        'popstate',
        () => {
          requestAnimationFrame(() => {
            if (document.activeElement !== document.body) return;
            if (restored instanceof HTMLElement && restored.isConnected) restored.focus({ preventScroll: true });
          });
        },
        { once: true }
      );
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
